import { router, usePage } from "@inertiajs/react"
import { createConsumer } from "@rails/actioncable"
import { useEffect, useRef } from "react"

import { createDebugLogger } from "@/lib/debug"

const log = createDebugLogger("Cable")

interface CableMessage {
  action: string
  task_id?: number
  list_id?: number
  task_status?: string
  timestamp: string
}

interface CableSubscription {
  unsubscribe(): void
}

interface ActionCableConsumer {
  subscriptions: {
    create(
      channel: { channel: string },
      callbacks: {
        connected?(): void
        disconnected?(): void
        received?(data: CableMessage): void
      },
    ): CableSubscription
  }
}

let consumer: ActionCableConsumer | null = null

function getConsumer(): ActionCableConsumer | null {
  if (typeof window === "undefined") return null
  consumer ??= createConsumer() as ActionCableConsumer
  return consumer
}

interface PageProps {
  list?: { id: number }
  currentListId?: number
  [key: string]: unknown
}

export function useCable() {
  const subscriptionRef = useRef<CableSubscription | null>(null)
  const page = usePage()

  useEffect(() => {
    const activeConsumer = getConsumer()
    if (!activeConsumer) return

    // Subscribe to the TasksChannel
    subscriptionRef.current = activeConsumer.subscriptions.create(
      { channel: "TasksChannel" },
      {
        connected(): void {
          log.log("Connected to TasksChannel")
        },

        disconnected(): void {
          log.log("Disconnected from TasksChannel")
        },

        received(data: CableMessage): void {
          log.log("Received message:", data)

          // Get current page props to check if we're viewing the affected list
          const currentProps = page.props as PageProps
          const currentListId =
            currentProps.list?.id ?? currentProps.currentListId

          // If the update affects the current list, refresh the page
          if (data.list_id && currentListId === data.list_id) {
            log.log("Refreshing page due to task update")

            // Use router.reload to refresh the current page with fresh data
            router.reload({
              only: ["tasks", "list"], // Only reload these props for efficiency
            })
          }
        },
      },
    )

    // Cleanup subscription on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
      }
    }
  }, [page.props])

  return { consumer: getConsumer() }
}

// Export a hook to use in list pages specifically
export function useTaskUpdates(listId?: number) {
  const subscriptionRef = useRef<CableSubscription | null>(null)

  useEffect(() => {
    if (!listId) return

    const activeConsumer = getConsumer()
    if (!activeConsumer) return

    subscriptionRef.current = activeConsumer.subscriptions.create(
      { channel: "TasksChannel" },
      {
        connected(): void {
          log.log(`Watching for updates to list ${listId}`)
        },

        received(data: CableMessage): void {
          // Only reload if this update affects our current list
          if (data.list_id === listId) {
            log.log(`List ${listId} updated, refreshing...`)

            router.reload({
              only: ["tasks", "list"],
            })
          }
        },
      },
    )

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
      }
    }
  }, [listId])
}

/**
 * Hook for the all-tasks view - refreshes on any task update.
 */
export function useAllTasksUpdates() {
  const subscriptionRef = useRef<CableSubscription | null>(null)

  useEffect(() => {
    const activeConsumer = getConsumer()
    if (!activeConsumer) return

    subscriptionRef.current = activeConsumer.subscriptions.create(
      { channel: "TasksChannel" },
      {
        connected(): void {
          log.log("All tasks view watching for updates")
        },

        received(data: CableMessage): void {
          if (data.action && data.task_id) {
            log.log("Task updated, refreshing all tasks...")

            router.reload({
              only: [
                "activeTasks",
                "completedTasks",
                "archivedTasks",
                "vitals",
              ],
            })
          }
        },
      },
    )

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe()
      }
    }
  }, [])
}
